import generatedProducts from "@/data/generatedProducts.json";
import generatedKnitwearManifest from "@/data/generatedKnitwearManifest.json";

const KNITWEAR_SIZES = ["XS", "S", "M", "L", "XL"];
const KNITWEAR_DESCRIPTION =
    "One-off handknit editorial piece sourced from the generated knitwear archive, with visible stitch texture and a soft sculptural silhouette for the FW26 collection.";
const TARGET_GENERATED_PRICE = 68;
const GENERATED_PRICE_STEP = 1;
const DEFAULT_CUSTOM_AMOUNT_MAX = 1000;
const VOUCHER_PRODUCTS = [
    {
        id: "voucher-tops",
        name: "Voucher",
        category: "tops",
        price: 1,
        description:
            "A custom voucher and gift card from UKnitwear, selectable from $1.00 to $1,000.00.",
        sizes: ["OS"],
        colors: ["Logo"],
        images: ["/logo.png"],
        badge: "VOUCHER",
        stock: 100,
        drop: "FW26",
        is_voucher: true,
        voucher_min_amount: 1,
        voucher_max_amount: 1000,
        custom_pricing_enabled: true,
        custom_amount_min: 1,
        custom_amount_max: 1000,
    },
    {
        id: "voucher-bottoms",
        name: "Voucher",
        category: "bottoms",
        price: 1,
        description:
            "A custom voucher and gift card from UKnitwear, selectable from $1.00 to $1,000.00.",
        sizes: ["OS"],
        colors: ["Logo"],
        images: ["/logo.png"],
        badge: "VOUCHER",
        stock: 100,
        drop: "FW26",
        is_voucher: true,
        voucher_min_amount: 1,
        voucher_max_amount: 1000,
        custom_pricing_enabled: true,
        custom_amount_min: 1,
        custom_amount_max: 1000,
    },
];
const GENERATED_KNITWEAR_NAMES = [
    "Breton Stripe Openwork Sweater",
    "Heritage Cable Cardigan",
    "Coastal Stripe Sweater",
    "Sailor Sleeve Sweater",
    "Ivory Cable Raglan Sweater",
    "UK Flag Sweater",
    "American Flag Sweater",
    "Kitty Cat Stripe Sweater",
    "Puppy Stripe Sweater",
    "Portrait Knit Sweater",
    "Star Print Sweater",
    "Sun Shirt Sweater",
    "Seaside Scene Sweater",
    "Knit Flowers Sweater",
    "Storybook Patchwork Sweater",
    "Moonlight V-Neck Sweater",
    "Sailboat Polo Sweater",
    "Black Cat Sweater",
    "Nordic Puppy Cardigan",
    "Minimal Stripe Sweater",
    "Daisy Garden Sweater",
    "Sunset Knit Sweater",
    "Alpine Skier Sweater",
    "Off-Shoulder Star Sweater",
    "Heart Garden Sweater",
    "Cable Pom Beanie",
    "Breton Stripe Beanie",
    "Cream Knit Gloves",
    "Openwork Arm Warmers",
    "Heritage Cable Scarf",
    "Ivory Lace Triangle Shawl",
];
const HIDDEN_GENERATED_KNITWEAR_IDS = new Set([
    "generated-knitwear-01",
    "generated-knitwear-02",
    "generated-knitwear-03",
    "generated-knitwear-21",
    "generated-knitwear-26",
    "generated-knitwear-27",
    "generated-knitwear-28",
    "generated-knitwear-29",
    "generated-knitwear-30",
    "generated-knitwear-31",
]);
const HIDDEN_IMPORTED_PRODUCT_IDS = new Set([
    "new-001",
    "new-006",
    "new-011",
    "new-016",
    "new-021",
    "new-026",
    "new-031",
    "new-036",
    "new-041",
    "new-046",
]);

function toNumericOrder(filename) {
    const match = filename.match(/(\d+)/);
    return match ? Number.parseInt(match[1], 10) : 0;
}

function buildVariantPrice(basePrice, index) {
    return basePrice + index * GENERATED_PRICE_STEP;
}

function withCustomPricing(product) {
    const normalizedPrice = Number(product.price) || 1;

    return {
        ...product,
        price: normalizedPrice,
        custom_pricing_enabled: product.custom_pricing_enabled ?? !product.is_voucher,
        custom_amount_min: product.custom_amount_min ?? normalizedPrice,
        custom_amount_max: product.custom_amount_max ?? DEFAULT_CUSTOM_AMOUNT_MAX,
    };
}

function buildGeneratedKnitwearProducts() {
    return [...generatedKnitwearManifest]
        .sort((a, b) => toNumericOrder(a) - toNumericOrder(b))
        .map((filename, index) => {
            const productNumber = String(index + 1).padStart(2, "0");
            const productName =
                GENERATED_KNITWEAR_NAMES[index] ?? `Handknit Editorial ${productNumber}`;

            return {
                id: `generated-knitwear-${productNumber}`,
                name: productName,
                category: "tops",
                price: buildVariantPrice(TARGET_GENERATED_PRICE, index),
                description: KNITWEAR_DESCRIPTION,
                sizes: KNITWEAR_SIZES,
                colors: ["Assorted"],
                images: [`/generated-knitwear-images/${filename}`],
                badge: "NEW",
                stock: 100,
                drop: "FW26",
            };
        });
}

export const catalogProducts = [
    ...VOUCHER_PRODUCTS,
    ...buildGeneratedKnitwearProducts(),
    ...generatedProducts.map((product, index) => ({
        ...product,
        price: buildVariantPrice(Number(product.price) || TARGET_GENERATED_PRICE, index),
    })),
].filter(
    (product) =>
        !HIDDEN_GENERATED_KNITWEAR_IDS.has(product.id) &&
        !HIDDEN_IMPORTED_PRODUCT_IDS.has(product.id)
).map(withCustomPricing);

export default catalogProducts;
